// Application entry: hash router, sign-in guard and view mounting.
// Styles are imported here so Vite bundles them (dev: injected, build: extracted).
import './styles/index.css';
import { curriculum } from './core/curriculum.js';
import { createProgress } from './core/progress.js';
import { store, adminStore, guestStore } from './core/store.js';
import { auth } from './auth/authService.js';
import { topbar, menuView, domainView, unitView } from './ui/views.js';
import { careerView } from './ui/learner-pages.js';
import { loginView, deniedView } from './ui/auth-views.js';
import { forgotPasswordView, resetPasswordView } from './ui/account-views.js';
import { createCloudSync } from './core/cloudSync.js';
import { adminDashboard, adminUnitTools, adminBanner } from './ui/admin.js';
import { h } from './ui/h.js';
import { mapView } from './ui/map-view.js';
import { parseMapRoute } from './map/mapState.js';
import { createSupportProgress } from './core/supportProgress.js';
import { supportView } from './ui/support.js';
import { publicLandingView } from './ui/public-landing.js';
import { claimView } from './ui/claim-view.js';
import { guestSummary } from './core/guestClaim.js';
import { createPageTransition } from './ui/page-transition.js';
import { mountReadingProgress } from './ui/reading-progress.js';
import { createTestimonials } from './core/testimonials.js';
import { createStore } from './core/store.js';
import { createRewards } from './ui/rewards.js';
import { createVerifiedSupport } from './core/verifiedSupport.js';
import { createVerifiedLabs } from './core/verifiedLabs.js';
import { initSound, play } from './ui/sound.js';
import { createCertificates } from './core/certs.js';
import { certificatesCard, certificatePage } from './ui/certificates-view.js';
import { CERTIFICATES } from './core/certificates.js';
import { questBoard, questStats, recordWeek, QUESTS_KEY } from './core/quests.js';
import { questCard, questToast } from './ui/quests-view.js';
import { medalStatus, lookOptions, LOOK_KEY } from './core/medals.js';
import { createIncidentProgress } from './core/incidentProgress.js';
import { incidentView, VERIFY_STATUS_TEXT, verifiedText } from './ui/incident-view.js';
import { incidentsPage, incidentsHomeCard } from './ui/incidents-view.js';
import { findIncident } from './incidents/index.js';

// Learner progress (unchanged keys) and the admin test sandbox are two separate
// progression engines over two separate storage namespaces.
const learnerProgress = createProgress(curriculum, store);
const adminProgress = createProgress(curriculum, adminStore, { bypass: !adminStore.get('prefs')?.enforce });
const learnerSupport = createSupportProgress(store);
const adminSupport = createSupportProgress(adminStore, { admin: true });
// Signed-out visitors work in their own temporary, untrusted guest record (nell:v1:guest:).
const guestSupport = createSupportProgress(guestStore);
// Incident Labs: their own local record, never `progress` (XP only from server-verified solves).
// An incident unlocks when its related domain is complete in the learner's own progress (like
// mission unlocks); the admin sandbox opens every incident.
const learnerIncidents = createIncidentProgress(store, { domainDone: (d) => learnerProgress.isDomainComplete(d) });
const adminIncidents = createIncidentProgress(adminStore);
const root = document.getElementById('app');
// Public (no sign-in): the landing page (#/), the free Support Desk (#/support), sign-in, create-account,
// and (real accounts) forgot / reset password.
const PUBLIC = new Set(['', 'login', 'signup', 'support', 'forgot', 'reset-password', 'cert']);
// Real accounts (Supabase): the learner record is also kept in the account (src/core/cloudSync.js).
// Admin accounts work in the local admin sandbox and are never synced.
const cloud = auth.accounts && auth.provider.client ? createCloudSync({ client: auth.provider.client, store }) : null;
// Testimonials (Profile → admin review → landing page). Real accounts: Supabase table +
// public function (0002 migration). Development mock: kept in this browser (nell:v1:dev:).
const testimonials = createTestimonials({ client: auth.accounts ? auth.provider.client || null : null, devStore: createStore('nell:v1:dev:'), user: () => auth.user() });
// Certificates of completion (issue-certificate function + 0004 migration; development sign-in:
// kept in this browser and labelled as not real). src/core/certs.js, src/ui/certificates-view.js
const certs = createCertificates({ client: auth.accounts ? auth.provider.client || null : null, devStore: createStore('nell:v1:dev:'), user: () => auth.user() });
// Rewards (XP pop-ups, level-up, one-time medal pop-ups) for signed-in learners — presentation only,
// recomputed from verified completion records after every learner-store write (src/ui/rewards.js).
initSound(store);
/** Title and avatar frame chosen on Profile, limited to what this learner's medals unlock. */
const lookNow = () => lookOptions(medalStatus({ progress: rewardProgress, curriculum, support: rewardSupport }), store.get(LOOK_KEY, {}));
const learnerId = () => { const s = auth.current(); return s && !auth.isAdmin() ? String(s.user?.id || s.user?.email || s.user?.name || 'learner') : null; };
// Server-verified Support Desk progress (real accounts only): the verify-support Edge Function
// re-runs each completed ticket on the server and records it in public.completions; rewards (XP,
// Support Desk Medal, Your week) count only those rows (src/core/verifiedSupport.js).
const verified = cloud ? createVerifiedSupport({ client: auth.provider.client, store, local: learnerSupport, userId: () => (auth.accounts && !auth.isAdmin() ? auth.user()?.id || null : null) }) : null;
const rewardSupport = verified ? verified.view : learnerSupport;
// Server-verified missions (phase 2: Domains 01–02 so far, SERVER_LAB_DOMAINS; real accounts only): the verify-lab Edge Function
// replays each completed mission and records it in public.completions; XP, levels, domain medals,
// titles and Your week count only those (src/core/verifiedLabs.js). Unlocking still uses the local record.
const learnerUser = () => (auth.accounts && !auth.isAdmin() ? auth.user()?.id || null : null);
const verifiedLabs = cloud ? createVerifiedLabs({ client: auth.provider.client, store, progress: learnerProgress, curriculum, userId: learnerUser }) : null;
const rewardProgress = verifiedLabs ? verifiedLabs.view : learnerProgress;
const rewards = createRewards({
  store, progress: rewardProgress, support: rewardSupport, curriculum, learnerId,
  openCabinet: () => go('#/profile'),
});
store.onChange(() => rewards.schedule());
verified?.onChange((e) => {
  if (e.type === 'loaded') {
    rewards.rebase();
    // pages that show XP or medals were drawn before the verified list arrived
    const p0 = location.hash.replace(/^#\/?/, '').split('/')[0];
    if (p0 === '' || p0 === 'profile') route();
  }
  if (e.type === 'verified') rewards.schedule();
  for (const el of document.querySelectorAll('[data-verify-id]')) paintVerify(el);
});
verifiedLabs?.onChange((e) => {
  if (e.type === 'loaded') {
    rewards.rebase();
    const p0 = location.hash.replace(/^#\/?/, '').split('/')[0];
    if (p0 === '' || p0 === 'profile') route();
  }
  if (e.type === 'verified' || e.type === 'incident-verified') rewards.schedule(); // incident XP: Phase 4
  for (const el of document.querySelectorAll('[data-lab-verify]')) paintLabVerify(el);
  for (const el of document.querySelectorAll('[data-incident-verify]')) paintIncidentVerify(el);
  if (e.type === 'checked' || e.type === 'loaded') for (const el of document.querySelectorAll('[data-testid="home-verify-notice"]')) el.replaceWith(labRedoNotice());
});
/**
 * The D01 onboarding gate (Support Desk first) applies only to learners with no mission history.
 * A unit record that is complete OR unlocked exists only after real progress: a completed mission,
 * the mission it unlocked, or a completed mission reset for "Redo to verify". Counting "unlocked"
 * keeps a learner who redoes their only completed mission from being sent to the Support Desk.
 */
const hasPriorLearnerProgress = () => Object.values(learnerProgress.raw().units || {}).some((r) => r.status === 'complete' || r.unlocked);
/** Mission page: "verified by RouteZero" status under a completed mission (real accounts, D01). */
function paintLabVerify(el) {
  const k = el.dataset.labVerify;
  const st = verifiedLabs?.stateOf(k);
  el.dataset.state = st || '';
  el.hidden = !st;
  if (st === 'redo') {
    const [d, u] = k.split('.');
    const label = h('span', { class: 'rz-verify-text' }, 'NOT VERIFIED — THIS MISSION WAS COMPLETED BEFORE SERVER CHECKS (OR ITS RECORD IS NOT ON THIS DEVICE), SO IT CAN’T BE CHECKED. REDO IT TO EARN ITS XP.');
    const btn = h('button', { class: 'btn small primary rz-verify-redo', type: 'button', 'data-testid': 'lab-redo' }, 'Redo to verify');
    btn.onclick = () => {
      if (btn.dataset.confirm !== 'yes') { btn.dataset.confirm = 'yes'; btn.textContent = 'Yes — restart this mission'; return; }
      learnerProgress.resetUnit(d, u); // this mission only; later missions stay unlocked and keep their work
      route();
    };
    el.replaceChildren(label, btn);
    return;
  }
  const blocker = st === 'waiting' ? verifiedLabs.blockerOf(k) : null;
  el.textContent = st === 'verified' ? '✓ VERIFIED BY ROUTEZERO' : st === 'checking' ? 'VERIFYING…'
    : st === 'waiting' ? `WAITING — VERIFIED AUTOMATICALLY ONCE MISSION ${String(blocker?.number || 1).padStart(2, '0')} IS VERIFIED`
      : st === 'unverified' ? 'NOT VERIFIED YET — XP IS ADDED ONCE THE SERVER CONFIRMS IT' : '';
}
/** Incident Labs (Phase 3): solved runs are sent for server replay only for a signed-in learner. */
const incidentVerifyOn = () => !!(verifiedLabs && learnerUser());
/** Incident page: verification status of a solved run (verified only after the server's explicit success). */
function incidentVerifyBadge(k) {
  const el = h('p', { class: 'rz-verify', 'data-incident-verify': k, 'data-testid': 'incident-verify-status' });
  paintIncidentVerify(el);
  return el;
}
function paintIncidentVerify(el) {
  const st = verifiedLabs?.incidentStatusOf(el.dataset.incidentVerify) || null;
  el.dataset.state = st || '';
  el.hidden = !st;
  el.textContent = st === 'verified' ? verifiedText(verifiedLabs.incidentXpOf(el.dataset.incidentVerify)) : st ? VERIFY_STATUS_TEXT[st] : '';
}
/** Home: which server-verified missions need a redo before they count (empty when none). */
function labRedoNotice() {
  const list = verifiedLabs && !auth.isAdmin() ? verifiedLabs.needsRedo() : [];
  if (!list.length) return h('div', { 'data-testid': 'home-verify-notice', hidden: true });
  const name = (k) => { const [d, u] = k.split('.'); const dom = curriculum.domains.find((x) => x.id === d); const i = dom.units.findIndex((x) => x.id === u); return { href: `#/unit/${d}/${u}`, label: `Domain ${dom.number} · Mission ${String(i + 1).padStart(2, '0')} · ${dom.units[i].title}` }; };
  return h('section', { class: 'rz-verify-notice panel', 'data-testid': 'home-verify-notice', role: 'note' },
    h('b', {}, `${list.length} mission${list.length === 1 ? '' : 's'} to redo`),
    h('p', {}, 'Missions are now checked by RouteZero’s server before they count toward XP, medals and titles. These were completed before that (or their record is not on this device), so redo them once — in order — to earn their XP again.'),
    h('ul', {}, list.slice(0, 6).map((k) => { const n = name(k); return h('li', {}, h('a', { href: n.href }, n.label)); })));
}
// ---------------------------------------------------------------- weekly quests
/** Home: this week's quests from verified results; celebrates (once) when all three are done. */
function questCardEl() {
  if (auth.isAdmin()) return null;
  const board = questBoard({ progress: rewardProgress, curriculum, support: rewardSupport, daily: store.get('daily', null) });
  const saved = store.get(QUESTS_KEY, {}) || {};
  if (board.complete && !saved[board.key]) {
    store.set(QUESTS_KEY, recordWeek(saved, board.key));
    const popups = typeof window !== 'undefined' && (!window.__NELL_FAST__ || window.__RZ_REWARDS__);
    if (popups) setTimeout(questToast, 500);
  }
  return questCard({ board, stats: questStats(store.get(QUESTS_KEY, {})) });
}
// solving today's daily challenge on Home moves the quests right away
store.onChange((key) => {
  if (key !== 'daily' && key !== 'progress' && key !== 'support-track') return;
  const el = document.querySelector('[data-testid="home-quests"]');
  if (el) { const next = questCardEl(); if (next) el.replaceWith(next); }
});
// ---------------------------------------------------------------- certificates
/** Progress toward each certificate from VERIFIED results (same items the server checks). */
function certProgressNow() {
  const s = rewardSupport.summary();
  const out = { support: { done: s.done, total: s.total, eligible: !!s.complete } };
  let done = s.done; let total = s.total; let all = !!s.complete;
  for (const d of curriculum.domains) {
    const ds = rewardProgress.domainSummary(d.id); const ok = rewardProgress.isDomainComplete(d.id);
    out[d.id] = { done: ds.done, total: ds.total, eligible: ok };
    done += ds.done; total += ds.total; all = all && ok;
  }
  out.phase1 = { done, total, eligible: all };
  return out;
}
/** Profile card: loads this learner's certificates, then lets them claim the ones they have earned. */
function certCardEl(user) {
  const host = h('div', { class: 'rz-certs-host' });
  const draw = (list, error = null) => host.replaceChildren(certificatesCard({
    certs: list, progress: certProgressNow(), defaultName: user?.name || '', error,
    onClaim: async (kind, name) => {
      const r = await certs.issue(kind, name, { eligible: !!certProgressNow()[kind]?.eligible });
      if (r.ok) { play('medal'); go(`#/cert/${r.certificate.id}`); }
      return r;
    },
  }));
  draw(null);
  certs.list().then((l) => draw(l), () => draw([], 'Could not load your certificates right now.'));
  return host;
}
/** Home: a short note when a certificate is earned but not claimed yet. */
function certNotice() {
  const host = h('div', { 'data-testid': 'home-cert-notice', hidden: true });
  if (auth.isAdmin()) return host;
  const p = certProgressNow();
  const ready = CERTIFICATES.filter((c) => p[c.kind]?.eligible);
  if (!ready.length) return host;
  certs.list().then((l) => {
    const open = ready.filter((c) => !l.some((x) => x.kind === c.kind));
    if (!open.length) return;
    host.hidden = false; host.className = 'rz-cert-notice panel';
    host.replaceChildren(h('b', {}, `🎓 Certificate ready to claim: ${open.map((c) => c.short).join(' · ')}`), h('a', { class: 'btn small primary', href: '#/profile', onclick: () => { setTimeout(() => document.getElementById('rz-certs')?.scrollIntoView({ block: 'start' }), 120); } }, 'Claim in Profile'));
  }, () => {});
  return host;
}
/** Support Desk: small "verified by RouteZero" status next to a completed ticket (real accounts). */
function paintVerify(el) {
  const id = el.dataset.verifyId;
  const st = verified?.stateOf(id);
  el.dataset.state = st || '';
  el.hidden = !st;
  if (st === 'redo') {
    // the saved work can't be checked by the server (older ticket steps, or it no longer passes):
    // offer to clear this one ticket and do it again — two clicks, nothing else is touched
    const label = h('span', { class: 'rz-verify-text' }, 'NOT VERIFIED — THIS TICKET WAS SAVED BEFORE AN UPDATE, SO THE SERVER CAN’T CHECK IT. REDO IT TO EARN ITS XP.');
    const btn = h('button', { class: 'btn small primary rz-verify-redo', type: 'button', 'data-testid': 'support-redo' }, 'Redo to verify');
    btn.onclick = () => {
      if (btn.dataset.confirm !== 'yes') { btn.dataset.confirm = 'yes'; btn.textContent = 'Yes — clear my old answers and redo it'; return; }
      const ok = id === 'TK-L1-10001' ? learnerSupport.redoPractice() : learnerSupport.redoTicket(id);
      if (ok) { supportSelection = id; route(); }
    };
    el.replaceChildren(label, btn);
    return;
  }
  const blocker = st === 'waiting' ? verified.blockerOf(id) : null;
  el.textContent = st === 'verified' ? '✓ VERIFIED BY ROUTEZERO' : st === 'checking' ? 'VERIFYING…'
    : st === 'waiting' ? `WAITING — VERIFIED AUTOMATICALLY ONCE TICKET ${blocker?.number || 1} IS VERIFIED`
      : st === 'unverified' ? 'NOT VERIFIED YET — XP IS ADDED ONCE THE SERVER CONFIRMS IT' : '';
}
// One-time message shown at the top of the next page (email links, sign-out problems).
let flash = null;
let supportSelection = null;
const SYNC_TEXT = { synced: 'Progress saved to your account', saving: 'Saving progress…', pending: 'Saving progress…', offline: 'Offline — progress is kept on this device and saved when you are back online', error: 'Could not reach your account — progress is kept on this device and will be saved automatically' };
// The mission map keeps its camera between map routes (#/map/...), so moving
// between map levels animates instead of re-rendering the page.
let mapCtl = null;
// Result of the last guest-progress claim, shown once on #/claim after the re-render.
let lastClaim = null;

/** #/support/<ticket id>: open that ticket if it is the practice or not locked. */
function linkedTicket(track, id) {
  if (id === 'TK-L1-10001') return id;
  return track.ticketState(id) === 'available' || track.ticketState(id) === 'complete' ? id : null;
}
/** Remember the learner's last ticket / lab for "Continue where you left off" (learner store; synced with the account). */
function rememberActivity(a) {
  const prev = store.get('last-activity');
  if (prev && prev.kind === a.kind && prev.id === a.id && prev.d === a.d && prev.u === a.u) return;
  store.set('last-activity', { ...a, at: Date.now() });
}

function route() {
  const parts = (location.hash.replace(/^#\/?/, '') || '').split('/').filter(Boolean);
  if (parts[0] !== 'support') supportSelection = null;
  const session = auth.current();
  // ---- sign-in guard
  if (!session && !PUBLIC.has(parts[0] || '')) {
    try { sessionStorage.setItem('nell:next', location.hash || '#/'); } catch { /* ignore */ }
    return go('#/login');
  }
  const admin = auth.isAdmin();
  const progress = admin ? adminProgress : learnerProgress;
  const support = admin ? adminSupport : learnerSupport;
  const isMapRoute = parts[0] === 'map' || parts[0] === 'missions';
  if (isMapRoute && mapCtl && mapCtl.admin === admin && document.body.contains(mapCtl.view.el)) {
    mapCtl.view.navigate(parseMapRoute(parts.slice(1)));
    return;
  }
  mapCtl?.view.destroy(); mapCtl = null;
  progress.reload();
  support.reload();
  guestSupport.reload();
  let crumbs = [{ label: 'Home', href: '#/' }];
  let view;
  if (!session && !parts[0]) {
    crumbs = [];
    view = publicLandingView(guestSupport, { testimonials });
  } else if (!session && parts[0] === 'support') {
    if (parts[1] && !supportSelection) supportSelection = linkedTicket(guestSupport, parts[1]);
    // Free Level 1 Support Desk: the same ticket engine and validation, but a separate
    // temporary guest record. Never the learner record, never the admin sandbox.
    crumbs = [];
    view = supportView(guestSupport, { admin: false, publicMode: true, initialSelected: supportSelection, onComplete: (id) => { supportSelection = id; route(); } });
  } else if (parts[0] === 'login' || parts[0] === 'signup') {
    if (session) return go(admin ? '#/admin' : '#/');
    crumbs = [];
    const notice = flash; flash = null;
    view = loginView(auth, {
      mode: parts[0] === 'signup' ? 'create' : 'login',
      guest: guestSummary(guestSupport),
      notice,
      testimonials,
      onDone: async (role) => {
        try { sessionStorage.removeItem('nell:next'); } catch { /* ignore */ }
        // real accounts: bring this account's saved progress onto the device first
        if (cloud && role !== 'admin') await cloud.attach(auth.user());
        // A learner with guest work in this browser is asked whether to continue where they left off.
        go(role === 'admin' ? '#/admin' : guestSummary(guestSupport).any ? '#/claim' : '#/');
      },
    });
  } else if (parts[0] === 'forgot') {
    if (session) return go('#/');
    if (!auth.accounts) return go('#/login');
    crumbs = [];
    view = forgotPasswordView(auth);
  } else if (parts[0] === 'reset-password') {
    if (!auth.accounts) return go('#/login');
    crumbs = [];
    view = resetPasswordView(auth, { onDone: async () => {
      flash = 'Your new password is saved.';
      if (cloud && !auth.isAdmin()) await cloud.attach(auth.user());
      go(auth.isAdmin() ? '#/admin' : '#/');
    } });
  } else if (parts[0] === 'claim') {
    // Guest → account: admin test mode never claims guest work.
    if (admin) return go('#/admin');
    crumbs.push({ label: 'Continue where you left off' });
    const result = lastClaim; lastClaim = null;
    view = claimView({ guest: guestSupport, learner: learnerSupport, go, result, onResult: (r) => { lastClaim = r; route(); } });
  } else if (parts[0] === 'admin') {
    crumbs.push({ label: 'Admin dashboard' });
    // Route protection: learners cannot open the dashboard, even by typing the URL.
    view = admin
      ? adminDashboard({ progress: adminProgress, learnerProgress, auth, curriculum, rerender: route, learnerStore: store, adminStore, testimonials })
      : deniedView('The admin dashboard');
  } else if (parts[0] === 'domain' && parts[1]) {
    const priorLearnerProgress = hasPriorLearnerProgress();
    if (!admin && parts[1] === 'd01' && !support.summary().complete && !priorLearnerProgress) return go('#/support');
    const d = curriculum.domains.find((x) => x.id === parts[1]);
    if (!d) return go('#/');
    crumbs.push({ label: `Domain ${d.number} — ${d.title}` });
    view = domainView(progress, d.id, route);
  } else if (parts[0] === 'unit' && parts[1] && parts[2]) {
    const priorLearnerProgress = hasPriorLearnerProgress();
    if (!admin && parts[1] === 'd01' && !support.summary().complete && !priorLearnerProgress) return go('#/support');
    const d = curriculum.domains.find((x) => x.id === parts[1]);
    const u = d?.units.find((x) => x.id === parts[2]);
    if (!u) return go('#/');
    crumbs.push({ label: `Domain ${d.number}`, href: `#/domain/${d.id}` }, { label: `${u.num || u.id} · ${u.title}` });
    if (!admin) rememberActivity({ kind: 'lab', d: d.id, u: u.id });
    view = unitView(progress, d.id, u.id, route, admin ? { admin: true, adminTools: (rt, fns) => adminUnitTools(rt, fns, { progress: adminProgress }) }
      : { verifyBadge: verifiedLabs?.covers(d.id) ? (k) => { const el = h('p', { class: 'rz-verify', 'data-lab-verify': k, 'data-testid': 'lab-verify' }); paintLabVerify(el); return el; } : null });
  } else if (parts[0] === 'incidents') {
    // #/incidents — the Incident Labs page; #/incidents/<id> — one incident (locked page until its domain is done)
    const incidents = admin ? adminIncidents : learnerIncidents;
    incidents.reload();
    if (!parts[1]) {
      crumbs.push({ label: 'Incident Labs' });
      view = incidentsPage(incidents, { serverVerify: !admin && incidentVerifyOn() });
    } else {
      const inc = findIncident(parts[1]);
      if (!inc || parts.length > 2) return go('#/incidents');
      crumbs.push({ label: 'Incident Labs', href: '#/incidents' }, { label: inc.title });
      view = admin ? incidentView(incidents, inc.id, route)
        : incidentView(incidents, inc.id, route, { serverVerify: incidentVerifyOn(), verifyStatus: incidentVerifyBadge, isVerified: () => !!verifiedLabs?.isIncidentVerified(`inc.${inc.id}`) });
      if (!admin && incidentVerifyOn()) verifiedLabs.check(); // send (or retry) a solved run that is not verified yet
    }
  } else if (parts[0] === 'career' || parts[0] === 'security') {
    // Career and the Security Labs preview are now sections of Profile & Career.
    return go('#/profile');
  } else if (parts[0] === 'cert') {
    crumbs.push({ label: 'Certificate' });
    view = certificatePage({ code: parts[1] || '', lookup: (id) => certs.lookup(id), signedIn: !!session && !admin });
  } else if (parts[0] === 'profile') {
    crumbs.push({ label: 'Profile & Career' });
    view = admin ? deniedView('The learner profile') : careerView(learnerProgress, session?.user, curriculum, learnerSupport, { testimonials, rewardSupport, rewardProgress, store, certCard: certCardEl(session?.user), look: lookNow(), onLook: () => route() });
  } else if (parts[0] === 'support') {
    crumbs.push({ label: 'Support Desk' });
    if (parts[1] && !supportSelection) supportSelection = linkedTicket(support, parts[1]);
    view = supportView(support, { admin, verifyBadge: verified && !admin ? (id) => { const el = h('p', { class: 'rz-verify', 'data-verify-id': id, 'data-testid': 'support-verify' }); paintVerify(el); return el; } : null, initialSelected: supportSelection, remember: !admin ? (id) => rememberActivity({ kind: 'support', id }) : null, onComplete: (id) => { supportSelection = id; route(); } });
  } else if (isMapRoute) {
    crumbs.push({ label: 'Mission Map' });
    const mv = mapView({ progress, curriculum, admin, support });
    mv.navigate(parseMapRoute(parts.slice(1)));
    mapCtl = { view: mv, admin };
    view = mv.el;
  } else {
    view = admin ? menuView(progress, session?.user) : menuView(progress, session?.user, learnerSupport, { last: store.get('last-activity'), labState: (k) => store.get(`lab:${k}`), rewardSupport, rewardProgress, store, look: lookNow(), notice: [labRedoNotice(), certNotice()], quests: questCardEl(), incidents: (learnerIncidents.reload(), incidentsHomeCard(learnerIncidents)) });
  }
  // components with timers/observers (landing carousels, ticket flow) register a cleanup
  for (const el of root.querySelectorAll('[data-cleanup]')) { try { el.__cleanup?.(); } catch { /* ignore */ } }
  root.textContent = '';
  root.append(
    topbar(crumbs, { session, active: parts[0] || '', progress, rewardProgress: admin ? progress : rewardProgress, curriculum, supportSummary: admin ? support.summary() : rewardSupport.summary(), look: session && !admin ? lookNow() : null, onLogout: signOut }),
    admin ? adminBanner() : '',
    flash && parts[0] !== 'login' ? h('div', { class: 'rz-flash', role: 'status', 'data-testid': 'flash' }, flash) : '',
    view,
    !session && !parts[0] ? '' : h('footer', { class: `foot ${store.persistent() ? '' : 'warn'}` }, 'RouteZero · Start with zero. Engineer your future.',
      store.persistent() ? '' : ' · ⚠ Browser storage is unavailable: progress will not survive a reload.',
      session && cloud && !admin ? h('span', { class: 'rz-sync', role: 'status', 'data-testid': 'sync-status', 'data-sync': cloud.status }, ` · ${SYNC_TEXT[cloud.status] || ''}`) : ''));
  if (parts[0] !== 'login') flash = null;
  window.scrollTo(0, 0);
  mountReadingProgress(root); // long pages (data-reading on main.page) get a reading-progress bar
  mapCtl?.view.mount();
  rewards.sync();
  // real accounts: fetch this learner's server-verified completions once per sign-in, then send any
  // local completion the server has not confirmed yet
  if (verified && session && !admin && verified.loadedFor !== auth.user()?.id && verified.status !== 'loading') verified.load().then((ok) => ok && verified.check());
  if (verifiedLabs && session && !admin && verifiedLabs.loadedFor !== auth.user()?.id && verifiedLabs.status !== 'loading') verifiedLabs.load().then((ok) => ok && verifiedLabs.check());
  if (parts[0] === 'profile' && rewards.takeCabinetScroll()) { const m = document.getElementById('rz-medals'); m?.scrollIntoView({ block: 'start' }); m?.focus({ preventScroll: true }); }
  // Exposed for automated tests and debugging.
  window.__nell = { progress, learnerProgress, adminProgress, learnerSupport, adminSupport, guestSupport, curriculum, view, auth, verified, verifiedLabs };
}
function go(hash) {
  if (location.hash === hash) route(); else location.hash = hash;
}

/** Sign out: save what is left to the account, clear this device's copy, end the session. */
async function signOut() {
  signingOut = true;
  const r = cloud ? await cloud.detach() : { ok: true };
  try { await auth.logout(); } finally { signingOut = false; }
  verified?.reset();
  verifiedLabs?.reset();
  if (!r.ok) flash = 'Some recent progress could not be saved to your account yet (offline). It stays on this device and is saved the next time you sign in here.';
  go('#/login');
}

// live sync status in the footer
cloud?.onStatus((st) => {
  const el = document.querySelector('[data-testid="sync-status"]');
  if (el) { el.dataset.sync = st; el.textContent = ` · ${SYNC_TEXT[st] || ''}`; }
});
// real accounts: signed out elsewhere (another tab, session expired) → back to a public page.
// Our own sign-out navigates by itself (signingOut), so it is not rendered twice.
let signingOut = false;
if (auth.accounts) auth.onChange((s) => { if (!s && !signingOut) route(); });

// Route changes stagger the old page's blocks out and the new page's blocks in
// (src/ui/page-transition.js). Map-to-map moves animate the map camera instead.
const isMapHash = () => ['map', 'missions'].includes(location.hash.replace(/^#\/?/, '').split('/')[0]);
const transition = createPageTransition(root, route,
  () => isMapHash() && !!mapCtl && mapCtl.admin === auth.isAdmin() && document.body.contains(mapCtl.view.el));
window.addEventListener('hashchange', transition.navigate);

/**
 * Start-up: restore the session (Supabase: also finishes email links — confirm address,
 * reset password), load the account's progress, then show the page.
 */
async function boot() {
  if (!auth.accounts) { route(); return; }
  root.replaceChildren(h('main', { class: 'page rz-boot', 'data-testid': 'boot', 'aria-busy': 'true' }, h('p', { class: 'muted' }, 'Loading RouteZero…')));
  try { await auth.init(); } catch { /* treated as signed out */ }
  // an error sent back in the URL hash (old-style links: #error=…&error_description=…)
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
  const hashError = hashParams.get('error_description');
  const clean = (hash) => history.replaceState(null, '', location.pathname + (hash || location.hash));
  if (location.search) clean();
  const s = auth.current();
  if (s && cloud && !auth.isAdmin()) await cloud.attach(s.user);
  if (auth.linkResult === 'reset' && s && !auth.linkError) { clean('#/reset-password'); }
  else if (auth.linkResult === 'reset') {
    // the code was already used, has expired, or the link was opened in another browser
    flash = 'That email link has expired or was already used. Sign in, or request a new link.';
    clean('#/login');
  }
  else if (auth.linkResult === 'confirmed') {
    flash = s ? `Email confirmed. Welcome to RouteZero, ${s.user.name}!` : 'Email confirmed. Sign in to continue.';
    clean(s ? (guestSummary(guestSupport).any ? '#/claim' : '#/') : '#/login');
  } else if (auth.linkError || hashError) {
    flash = /expired|invalid/i.test(auth.linkError || hashError || '') ? 'That email link has expired or was already used. Sign in, or request a new link.' : `That email link did not work: ${auth.linkError || hashError}`;
    clean('#/login');
  }
  route();
}
boot();
